/**
 * Guide Page - Mortgage Guide Wizard
 * Step-by-step mortgage guide with personalized content
 */

import { storage, requireAuth, requireProfile } from '../utils/storage.js';
import { renderNavbar } from '../components/navbar.js';
import { renderFooter } from '../components/footer.js';
import {
  calculateMaxMortgagePayment,
  calculateDownPayment,
  calculateExpenses,
  formatCurrency,
  formatPercentage
} from '../utils/calculations.js';

const GUIDE_STEPS = [
  {
    id: 1,
    title: 'Preparación financiera',
    icon: 'shield',
    description: 'Evalúa tu situación antes de empezar'
  },
  {
    id: 2,
    title: 'Ahorros necesarios',
    icon: 'piggy-bank',
    description: 'Calcula cuánto necesitas ahorrar'
  },
  {
    id: 3,
    title: 'Tipos de hipoteca',
    icon: 'file-text',
    description: 'Entiende tus opciones'
  },
  {
    id: 4,
    title: 'Gastos reales',
    icon: 'credit-card',
    description: 'Conoce todos los costes'
  },
  {
    id: 5,
    title: 'Próximos pasos',
    icon: 'check-circle',
    description: 'Tu checklist personal'
  }
];

export function renderGuide(container) {
  if (!requireAuth()) return;
  if (!requireProfile()) return;

  const profile = storage.getProfile();
  const progress = storage.getGuideProgress();
  const currentStep = progress.step;

  container.innerHTML = `
    ${renderNavbar()}
    
    <main class="page guide-page">
      <div class="container">
        <div class="wizard animate-fadeIn">
          <!-- Progress -->
          <div class="wizard-progress">
            ${GUIDE_STEPS.map((step, index) => `
              <div class="wizard-step ${currentStep > step.id ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}">
                <div class="wizard-step-number">${currentStep > step.id ? '' : step.id}</div>
                <span class="wizard-step-label">${step.title}</span>
              </div>
            `).join('')}
          </div>
          
          <!-- Content -->
          <div class="wizard-content" id="wizardContent">
            ${renderStepContent(currentStep, profile)}
          </div>
          
          <!-- Actions -->
          <div class="wizard-actions">
            <button 
              class="btn btn-secondary" 
              id="prevBtn"
              ${currentStep === 1 ? 'disabled style="opacity: 0.5;"' : ''}
            >
              <i data-feather="arrow-left"></i>
              Anterior
            </button>
            
            <div style="display: flex; align-items: center; gap: 8px;">
              <span style="color: var(--gray-500); font-size: 14px;">
                Paso ${currentStep} de ${GUIDE_STEPS.length}
              </span>
            </div>
            
            <button class="btn btn-primary" id="nextBtn">
              ${currentStep === GUIDE_STEPS.length ? 'Finalizar' : 'Siguiente'}
              <i data-feather="${currentStep === GUIDE_STEPS.length ? 'check' : 'arrow-right'}"></i>
            </button>
          </div>
        </div>
      </div>
    </main>
    
    ${renderFooter()}

    <style>
      .guide-page {
        padding-top: 100px;
        min-height: 100vh;
        background: 
          radial-gradient(circle at 20px 20px, var(--primary-100) 2px, transparent 0),
          linear-gradient(135deg, var(--primary-50) 0%, white 100%);
        background-size: 40px 40px, 100% 100%;
        background-attachment: fixed;
      }

      .wizard-content {
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.5);
      }
    </style>
  `;

  initGuideNavigation(currentStep, profile);
}

function renderStepContent(step, profile) {
  const maxPayment = calculateMaxMortgagePayment(profile.monthlyIncome);
  const maxPayment35 = profile.monthlyIncome * 0.35;

  switch (step) {
    case 1:
      return renderStep1(profile, maxPayment, maxPayment35);
    case 2:
      return renderStep2(profile);
    case 3:
      return renderStep3();
    case 4:
      return renderStep4();
    case 5:
      return renderStep5(profile);
    default:
      return '';
  }
}

function renderStep1(profile, maxPayment, maxPayment35) {
  const isHighRisk = profile.employmentStatus === 'unemployed' || profile.employmentStatus === 'temporary';

  return `
    <h2 class="wizard-title">
      <i data-feather="shield" style="color: var(--primary-600); margin-right: 8px;"></i>
      Paso 1: Preparación financiera
    </h2>
    
    <div class="alert ${isHighRisk ? 'alert-warning' : 'alert-info'}" style="margin-bottom: 24px;">
      <i data-feather="${isHighRisk ? 'alert-triangle' : 'info'}"></i>
      <div>
        ${isHighRisk
      ? '<strong>Atención:</strong> Tu situación laboral puede dificultar la aprobación de una hipoteca. Los bancos prefieren contratos indefinidos con al menos 1 año de antigüedad.'
      : 'Tu situación laboral es favorable para solicitar una hipoteca. Aún así, asegúrate de cumplir con los requisitos básicos.'
    }
      </div>
    </div>
    
    <div style="margin-bottom: 32px;">
      <h3 style="font-size: 18px; margin-bottom: 16px;">📊 La regla del 30-35%</h3>
      <p style="color: var(--gray-600); line-height: 1.8; margin-bottom: 16px;">
        Los bancos en España utilizan la <strong>regla del 30-35%</strong> para determinar cuánto puedes 
        pagar de hipoteca mensualmente. Esto significa que tu cuota no debería superar el 30-35% 
        de tus ingresos netos.
      </p>
      
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px;">
        <div style="background: var(--primary-50); padding: 20px; border-radius: var(--radius-lg); text-align: center;">
          <div style="font-size: 12px; color: var(--primary-600); margin-bottom: 4px;">Cuota máxima (30%)</div>
          <div style="font-size: 28px; font-weight: 700; color: var(--primary-700);">${formatCurrency(maxPayment)}</div>
          <div style="font-size: 12px; color: var(--gray-500);">Recomendado</div>
        </div>
        <div style="background: var(--gray-100); padding: 20px; border-radius: var(--radius-lg); text-align: center;">
          <div style="font-size: 12px; color: var(--gray-600); margin-bottom: 4px;">Cuota máxima (35%)</div>
          <div style="font-size: 28px; font-weight: 700; color: var(--gray-700);">${formatCurrency(maxPayment35)}</div>
          <div style="font-size: 12px; color: var(--gray-500);">Límite máximo</div>
        </div>
      </div>
    </div>
    
    <div style="margin-bottom: 24px;">
      <h3 style="font-size: 18px; margin-bottom: 16px;">✅ Requisitos básicos para solicitar hipoteca</h3>
      <ul style="list-style: none;">
        <li style="display: flex; align-items: flex-start; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--gray-100);">
          <span style="color: var(--success);">✓</span>
          <div>
            <strong>Estabilidad laboral</strong>
            <p style="color: var(--gray-500); font-size: 14px; margin: 0;">Contrato indefinido con mínimo 6-12 meses de antigüedad (o 2-3 años como autónomo)</p>
          </div>
        </li>
        <li style="display: flex; align-items: flex-start; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--gray-100);">
          <span style="color: var(--success);">✓</span>
          <div>
            <strong>Historial crediticio limpio</strong>
            <p style="color: var(--gray-500); font-size: 14px; margin: 0;">Sin aparecer en ficheros de morosos (ASNEF, RAI, etc.)</p>
          </div>
        </li>
        <li style="display: flex; align-items: flex-start; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--gray-100);">
          <span style="color: var(--success);">✓</span>
          <div>
            <strong>Capacidad de pago demostrable</strong>
            <p style="color: var(--gray-500); font-size: 14px; margin: 0;">Nóminas, declaraciones de renta y movimientos bancarios estables</p>
          </div>
        </li>
        <li style="display: flex; align-items: flex-start; gap: 12px; padding: 12px 0;">
          <span style="color: var(--success);">✓</span>
          <div>
            <strong>Ahorros suficientes</strong>
            <p style="color: var(--gray-500); font-size: 14px; margin: 0;">Al menos 20-30% del valor de la vivienda para entrada y gastos</p>
          </div>
        </li>
      </ul>
    </div>
    
    <div class="alert alert-success">
      <i data-feather="check-circle"></i>
      <div>
        <strong>Tu situación:</strong> Con unos ingresos de ${formatCurrency(profile.monthlyIncome)}/mes, 
        puedes aspirar a una cuota de hasta ${formatCurrency(maxPayment)}/mes.
      </div>
    </div>
  `;
}

function renderStep2(profile) {
  const targetPrices = [150000, 200000, 250000, 300000];

  return `
    <h2 class="wizard-title">
      <i data-feather="piggy-bank" style="color: var(--primary-600); margin-right: 8px;"></i>
      Paso 2: Ahorros necesarios
    </h2>
    
    <p style="color: var(--gray-600); line-height: 1.8; margin-bottom: 24px;">
      Para comprar una vivienda necesitas tener ahorrado aproximadamente el <strong>30-32%</strong> del precio:
    </p>
    
    <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 32px;">
      <div style="background: linear-gradient(135deg, var(--primary-500), var(--primary-700)); color: white; padding: 24px; border-radius: var(--radius-xl);">
        <div style="font-size: 14px; opacity: 0.9; margin-bottom: 8px;">Entrada (20%)</div>
        <div style="font-size: 14px; opacity: 0.8;">
          Los bancos financian hasta el 80% del valor de tasación. Tú debes aportar el 20% restante.
        </div>
      </div>
      <div style="background: linear-gradient(135deg, var(--secondary-500), var(--secondary-700)); color: white; padding: 24px; border-radius: var(--radius-xl);">
        <div style="font-size: 14px; opacity: 0.9; margin-bottom: 8px;">Gastos (10-12%)</div>
        <div style="font-size: 14px; opacity: 0.8;">
          Notaría, registro, impuestos (ITP o IVA+AJD) y tasación no están incluidos en la hipoteca.
        </div>
      </div>
    </div>
    
    <h3 style="font-size: 18px; margin-bottom: 16px;">💰 Comparativa según precio de vivienda</h3>
    
    <div class="table-container" style="margin-bottom: 24px;">
      <table>
        <thead>
          <tr>
            <th>Precio vivienda</th>
            <th>Entrada (20%)</th>
            <th>Gastos (12%)</th>
            <th>Total necesario</th>
          </tr>
        </thead>
        <tbody>
          ${targetPrices.map(price => {
    const entry = price * 0.20;
    const expenses = price * 0.12;
    const total = entry + expenses;
    const diff = total - profile.currentSavings;
    return `
              <tr>
                <td><strong>${formatCurrency(price)}</strong></td>
                <td>${formatCurrency(entry)}</td>
                <td>${formatCurrency(expenses)}</td>
                <td>
                  <strong>${formatCurrency(total)}</strong>
                  ${diff <= 0
        ? '<span class="badge badge-success" style="margin-left: 8px;">✓ Lo tienes</span>'
        : `<span style="color: var(--gray-500); font-size: 12px; display: block;">Te faltan ${formatCurrency(diff)}</span>`
      }
                </td>
              </tr>
            `;
  }).join('')}
        </tbody>
      </table>
    </div>
    
    <div class="card" style="background: var(--gray-50); border: 2px dashed var(--gray-200);">
      <div style="display: flex; align-items: center; gap: 16px;">
        <div style="width: 56px; height: 56px; background: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: var(--shadow-md);">
          <span style="font-size: 24px;">🎯</span>
        </div>
        <div>
          <div style="font-weight: 600; margin-bottom: 4px;">Tus ahorros actuales</div>
          <div style="font-size: 28px; font-weight: 700; color: var(--primary-600);">${formatCurrency(profile.currentSavings)}</div>
        </div>
      </div>
    </div>
  `;
}

function renderStep3() {
  return `
    <h2 class="wizard-title">
      <i data-feather="file-text" style="color: var(--primary-600); margin-right: 8px;"></i>
      Paso 3: Tipos de hipoteca
    </h2>
    
    <p style="color: var(--gray-600); line-height: 1.8; margin-bottom: 24px;">
      Existen tres tipos principales de hipoteca en España. Cada una tiene sus ventajas e inconvenientes:
    </p>
    
    <div style="display: flex; flex-direction: column; gap: 20px;">
      <!-- Hipoteca Fija -->
      <div class="card" style="border-left: 4px solid var(--primary-500);">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
          <h3 style="font-size: 18px; display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 24px;">🔒</span>
            Hipoteca a tipo FIJO
          </h3>
          <span class="badge badge-primary">Recomendada</span>
        </div>
        <p style="color: var(--gray-600); margin-bottom: 16px;">
          El tipo de interés se mantiene igual durante toda la vida del préstamo. 
          Pagas siempre la misma cuota.
        </p>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
          <div>
            <div style="color: var(--success); font-weight: 600; font-size: 14px; margin-bottom: 8px;">✓ Ventajas</div>
            <ul style="font-size: 14px; color: var(--gray-600); margin-left: 16px;">
              <li>Cuota estable y predecible</li>
              <li>Protección ante subidas del Euríbor</li>
              <li>Facilita la planificación financiera</li>
            </ul>
          </div>
          <div>
            <div style="color: var(--error); font-weight: 600; font-size: 14px; margin-bottom: 8px;">✗ Inconvenientes</div>
            <ul style="font-size: 14px; color: var(--gray-600); margin-left: 16px;">
              <li>Tipo inicial más alto que variable</li>
              <li>No te beneficias de bajadas del Euríbor</li>
              <li>Comisión por amortización anticipada</li>
            </ul>
          </div>
        </div>
        <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--gray-100); font-size: 14px; color: var(--gray-500);">
          <strong>Tipo actual:</strong> Entre 2,5% y 3,5% TIN aproximadamente
        </div>
      </div>
      
      <!-- Hipoteca Variable -->
      <div class="card" style="border-left: 4px solid var(--warning);">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
          <h3 style="font-size: 18px; display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 24px;">📈</span>
            Hipoteca a tipo VARIABLE
          </h3>
          <span class="badge badge-warning">Mayor riesgo</span>
        </div>
        <p style="color: var(--gray-600); margin-bottom: 16px;">
          El tipo de interés se revisa periódicamente (cada 6 o 12 meses) 
          según el Euríbor + un diferencial.
        </p>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
          <div>
            <div style="color: var(--success); font-weight: 600; font-size: 14px; margin-bottom: 8px;">✓ Ventajas</div>
            <ul style="font-size: 14px; color: var(--gray-600); margin-left: 16px;">
              <li>Tipo inicial más bajo</li>
              <li>Te beneficias si baja el Euríbor</li>
              <li>Menos comisión por amortización</li>
            </ul>
          </div>
          <div>
            <div style="color: var(--error); font-weight: 600; font-size: 14px; margin-bottom: 8px;">✗ Inconvenientes</div>
            <ul style="font-size: 14px; color: var(--gray-600); margin-left: 16px;">
              <li>Cuota impredecible</li>
              <li>Riesgo ante subidas del Euríbor</li>
              <li>Difícil planificación a largo plazo</li>
            </ul>
          </div>
        </div>
        <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--gray-100); font-size: 14px; color: var(--gray-500);">
          <strong>Tipo actual:</strong> Euríbor + 0,5% a 1% diferencial
        </div>
      </div>
      
      <!-- Hipoteca Mixta -->
      <div class="card" style="border-left: 4px solid var(--secondary-500);">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
          <h3 style="font-size: 18px; display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 24px;">⚖️</span>
            Hipoteca MIXTA
          </h3>
          <span class="badge" style="background: var(--secondary-100); color: var(--secondary-700);">Equilibrada</span>
        </div>
        <p style="color: var(--gray-600); margin-bottom: 16px;">
          Combina un período inicial a tipo fijo (5-15 años) y después 
          pasa a tipo variable hasta el final.
        </p>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
          <div>
            <div style="color: var(--success); font-weight: 600; font-size: 14px; margin-bottom: 8px;">✓ Ventajas</div>
            <ul style="font-size: 14px; color: var(--gray-600); margin-left: 16px;">
              <li>Estabilidad inicial garantizada</li>
              <li>Tipo inicial competitivo</li>
              <li>Flexibilidad a largo plazo</li>
            </ul>
          </div>
          <div>
            <div style="color: var(--error); font-weight: 600; font-size: 14px; margin-bottom: 8px;">✗ Inconvenientes</div>
            <ul style="font-size: 14px; color: var(--gray-600); margin-left: 16px;">
              <li>Incertidumbre tras período fijo</li>
              <li>Puede no ser la mejor opción en ningún escenario</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderStep4() {
  return `
    <h2 class="wizard-title">
      <i data-feather="credit-card" style="color: var(--primary-600); margin-right: 8px;"></i>
      Paso 4: Gastos reales de la compra
    </h2>
    
    <p style="color: var(--gray-600); line-height: 1.8; margin-bottom: 24px;">
      Además de la entrada, hay varios gastos asociados a la compra que debes conocer y presupuestar:
    </p>
    
    <div style="display: flex; flex-direction: column; gap: 16px;">
      <div style="display: flex; align-items: flex-start; gap: 16px; padding: 20px; background: var(--gray-50); border-radius: var(--radius-lg);">
        <div style="width: 48px; height: 48px; background: white; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; box-shadow: var(--shadow-sm);">
          <span style="font-size: 24px;">🏛️</span>
        </div>
        <div style="flex: 1;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <h4 style="font-size: 16px; margin-bottom: 4px;">Notaría</h4>
            <span style="font-weight: 600; color: var(--primary-600);">800€ - 1.200€</span>
          </div>
          <p style="font-size: 14px; color: var(--gray-500); margin: 0;">
            Escrituración de la compraventa y de la hipoteca. Regulado por arancel oficial.
          </p>
        </div>
      </div>
      
      <div style="display: flex; align-items: flex-start; gap: 16px; padding: 20px; background: var(--gray-50); border-radius: var(--radius-lg);">
        <div style="width: 48px; height: 48px; background: white; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; box-shadow: var(--shadow-sm);">
          <span style="font-size: 24px;">📋</span>
        </div>
        <div style="flex: 1;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <h4 style="font-size: 16px; margin-bottom: 4px;">Registro de la Propiedad</h4>
            <span style="font-weight: 600; color: var(--primary-600);">400€ - 700€</span>
          </div>
          <p style="font-size: 14px; color: var(--gray-500); margin: 0;">
            Inscripción de la vivienda y la hipoteca a tu nombre. También regulado por arancel.
          </p>
        </div>
      </div>
      
      <div style="display: flex; align-items: flex-start; gap: 16px; padding: 20px; background: var(--gray-50); border-radius: var(--radius-lg);">
        <div style="width: 48px; height: 48px; background: white; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; box-shadow: var(--shadow-sm);">
          <span style="font-size: 24px;">📊</span>
        </div>
        <div style="flex: 1;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <h4 style="font-size: 16px; margin-bottom: 4px;">Tasación</h4>
            <span style="font-weight: 600; color: var(--primary-600);">250€ - 500€</span>
          </div>
          <p style="font-size: 14px; color: var(--gray-500); margin: 0;">
            Valoración oficial de la vivienda requerida por el banco para conceder la hipoteca.
          </p>
        </div>
      </div>
      
      <div style="display: flex; align-items: flex-start; gap: 16px; padding: 20px; background: linear-gradient(135deg, rgba(26, 82, 118, 0.08), rgba(26, 158, 111, 0.08)); border-radius: var(--radius-lg); border: 1px solid var(--primary-200);">
        <div style="width: 48px; height: 48px; background: white; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; box-shadow: var(--shadow-sm);">
          <span style="font-size: 24px;">🏦</span>
        </div>
        <div style="flex: 1;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <h4 style="font-size: 16px; margin-bottom: 4px;">Impuestos (ITP o IVA+AJD)</h4>
            <span style="font-weight: 600; color: var(--primary-600);">6% - 10%</span>
          </div>
          <p style="font-size: 14px; color: var(--gray-500); margin: 0;">
            <strong>Vivienda de segunda mano:</strong> ITP (6-10% según CCAA)<br>
            <strong>Vivienda nueva:</strong> IVA (10%) + AJD (0,5-1,5%)
          </p>
        </div>
      </div>
    </div>
    
    <div class="alert alert-info" style="margin-top: 24px;">
      <i data-feather="info"></i>
      <div>
        <strong>💡 Consejo:</strong> Desde la Ley Hipotecaria de 2019, el banco paga la mayoría de gastos 
        de la hipoteca (notaría de hipoteca, registro de hipoteca, gestoría e impuesto AJD de hipoteca). 
        Tú solo pagas los gastos de la compraventa.
      </div>
    </div>
  `;
}

function renderStep5(profile) {
  return `
    <h2 class="wizard-title">
      <i data-feather="check-circle" style="color: var(--success); margin-right: 8px;"></i>
      Paso 5: Próximos pasos
    </h2>
    
    <div class="alert alert-success" style="margin-bottom: 24px;">
      <i data-feather="award"></i>
      <div>
        <strong>¡Enhorabuena!</strong> Has completado la guía hipotecaria. 
        Aquí tienes tu checklist personalizado para los próximos pasos.
      </div>
    </div>
    
    <div style="margin-bottom: 32px;">
      <h3 style="font-size: 18px; margin-bottom: 16px;">📋 Tu checklist personal</h3>
      
      <div style="display: flex; flex-direction: column; gap: 12px;">
        <label class="checklist-item">
          <input type="checkbox" class="checklist-checkbox">
          <span class="checklist-checkmark"></span>
          <span>Revisar mi historial crediticio (solicitar informe CIRBE al Banco de España)</span>
        </label>
        
        <label class="checklist-item">
          <input type="checkbox" class="checklist-checkbox">
          <span class="checklist-checkmark"></span>
          <span>Solicitar ofertas vinculantes en al menos 3 bancos diferentes</span>
        </label>
        
        <label class="checklist-item">
          <input type="checkbox" class="checklist-checkbox">
          <span class="checklist-checkmark"></span>
          <span>Comparar TAE, no solo el tipo de interés nominal (TIN)</span>
        </label>
        
        <label class="checklist-item">
          <input type="checkbox" class="checklist-checkbox">
          <span class="checklist-checkmark"></span>
          <span>Revisar productos vinculados (seguros, tarjetas, etc.) y su coste real</span>
        </label>
        
        <label class="checklist-item">
          <input type="checkbox" class="checklist-checkbox">
          <span class="checklist-checkmark"></span>
          <span>Consultar ayudas disponibles en ${profile.region}</span>
        </label>
        
        ${profile.age < 36 ? `
          <label class="checklist-item">
            <input type="checkbox" class="checklist-checkbox">
            <span class="checklist-checkmark"></span>
            <span>Informarme sobre el Aval ICO para jóvenes (primera vivienda)</span>
          </label>
        ` : ''}
        
        <label class="checklist-item">
          <input type="checkbox" class="checklist-checkbox">
          <span class="checklist-checkmark"></span>
          <span>Mejorar mi capacidad de ahorro (ver Plan de Ahorro)</span>
        </label>
        
        <label class="checklist-item">
          <input type="checkbox" class="checklist-checkbox">
          <span class="checklist-checkmark"></span>
          <span>Mantener estabilidad laboral y evitar nuevas deudas</span>
        </label>
      </div>
    </div>
    
    <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px;">
      <a href="/ahorro" data-link class="card" style="text-decoration: none; text-align: center;">
        <div style="font-size: 32px; margin-bottom: 8px;">💰</div>
        <h4 style="margin-bottom: 4px;">Plan de Ahorro</h4>
        <p style="font-size: 14px; color: var(--gray-500); margin: 0;">Consejos para acelerar tu ahorro</p>
      </a>
      
      <a href="/ayudas" data-link class="card" style="text-decoration: none; text-align: center;">
        <div style="font-size: 32px; margin-bottom: 8px;">🎁</div>
        <h4 style="margin-bottom: 4px;">Ayudas Públicas</h4>
        <p style="font-size: 14px; color: var(--gray-500); margin: 0;">Subvenciones en ${profile.region}</p>
      </a>
    </div>
    
    <style>
      .checklist-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px;
        background: var(--gray-50);
        border-radius: var(--radius-lg);
        cursor: pointer;
        transition: var(--transition-fast);
      }
      
      .checklist-item:hover {
        background: var(--gray-100);
      }
      
      .checklist-checkbox {
        display: none;
      }
      
      .checklist-checkmark {
        width: 24px;
        height: 24px;
        border: 2px solid var(--gray-300);
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: var(--transition-fast);
      }
      
      .checklist-checkbox:checked + .checklist-checkmark {
        background: var(--success);
        border-color: var(--success);
      }
      
      .checklist-checkbox:checked + .checklist-checkmark::after {
        content: '✓';
        color: white;
        font-weight: bold;
      }
      
      .checklist-checkbox:checked ~ span:last-child {
        text-decoration: line-through;
        color: var(--gray-400);
      }
    </style>
  `;
}

function initGuideNavigation(currentStep, profile) {
  const prevBtn = document.getElementById('prevBtn');
  const nextBtn = document.getElementById('nextBtn');
  const wizardContent = document.getElementById('wizardContent');

  prevBtn.addEventListener('click', () => {
    if (currentStep > 1) {
      const newStep = currentStep - 1;
      storage.setGuideProgress({ step: newStep, completed: false });
      window.router.navigate('/guia');
    }
  });

  nextBtn.addEventListener('click', () => {
    if (currentStep < GUIDE_STEPS.length) {
      const newStep = currentStep + 1;
      storage.setGuideProgress({ step: newStep, completed: newStep === GUIDE_STEPS.length });
      window.router.navigate('/guia');
    } else {
      // Completed - go to dashboard
      storage.setGuideProgress({ step: GUIDE_STEPS.length, completed: true });
      window.router.navigate('/dashboard');
    }
  });
}
